function debounce(fn, wait) {
  let t;
  return (...args) => {
    args[0]?.target.classList.remove("error-input");
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

let notifierPromise = null;
function getNotifier() {
  if (!notifierPromise) {
    const src =
      (window.importJs && window.importJs.notification) ||
      "@NextSkyTheme/notification";
    notifierPromise = import(src).catch(() => null);
  }
  return notifierPromise;
}

function getInlineContainer(input) {
  return (
    input.closest(".notification-wrapper") ||
    input.closest(".field") ||
    input.parentElement ||
    input.closest("form")
  );
}

function getValidationMessage(input) {
  if (input.validationMessage) return input.validationMessage;

  const validate = window.validate || {};
  const type =
    input.tagName.toLowerCase() === "textarea"
      ? "textarea"
      : input.tagName.toLowerCase() === "select"
      ? "select"
      : input.type;

  if (type === "tel") {
    return validate.phone_invalid || "Please enter a valid phone number.";
  }
  if (type === "email") {
    return (
      validate.emailRegex ||
      validate.email_invalid ||
      "Please enter a valid email address."
    );
  }
  return "Please fill out this field.";
}

function positionInlineNotice(container, input) {
  const notice = container.querySelector(".notification_inline");
  if (!notice) return;

  if (!window.matchMedia("(min-width: 768px)").matches) {
    notice.style.removeProperty("--message-right");
    notice.style.removeProperty("--message-top");
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const inputRect = input.getBoundingClientRect();
  const containerStyle = getComputedStyle(container);
  const borderRight = parseFloat(containerStyle.borderRightWidth) || 0;
  const borderTop = parseFloat(containerStyle.borderTopWidth) || 0;

  const right = containerRect.right - borderRight - inputRect.right;
  const top = inputRect.bottom - containerRect.top - borderTop + 6;

  notice.style.setProperty("--message-right", `${right}px`);
  notice.style.setProperty("--message-top", `${top}px`);
}

let lastValidationNoticeAt = 0;
function notifyValidation(input) {
  const now = Date.now();
  if (now - lastValidationNoticeAt < 400) return;
  lastValidationNoticeAt = now;

  const message = getValidationMessage(input);
  const container = getInlineContainer(input);
  if (!container) return;

  getNotifier().then((mod) => {
    if (!mod || !mod.notifierInline) return;
    const form = input.closest("form");
    if (form) {
      form
        .querySelectorAll(".notification_inline")
        .forEach((el) => el.remove());
    }
    container.classList.add("validation-anchor");
    mod.notifierInline.showElement(message, container, "warning", 4000);
    positionInlineNotice(container, input);
  });
}
function validateRequiredInputs(container = document) {
  const requiredInputs = container.querySelectorAll(`
    input[required][type="text"],
    input[required][type="email"], 
    input[required][type="password"],
    input[required][type="tel"],
    textarea[required],
    select[required]
  `);

  let isValid = true;
  const inputGroups = {
    text: [],
    email: [],
    tel: [],
    password: [],
    textarea: [],
    select: [],
  };

  requiredInputs.forEach((input) => {
    const type =
      input.tagName.toLowerCase() === "textarea"
        ? "textarea"
        : input.tagName.toLowerCase() === "select"
        ? "select"
        : input.type;
    if (inputGroups[type]) {
      inputGroups[type].push(input);
    }
  });

  Object.keys(inputGroups).forEach((type) => {
    inputGroups[type].forEach((input) => {
      const inputIsValid = validateInputByType(input, type);
      if (!inputIsValid) {
        isValid = false;
      }
    });
  });

  return isValid;
}

function validateInputByType(input, type) {
  let isValid = true;

  if (input.value.trim()) {
    switch (type) {
      case "email":
        if (!isValidEmail(input.value)) {
          isValid = false;
        }
        break;
      case "tel":
        if (!isValidPhone(input.value)) {
          isValid = false;
        }
        break;
      case "text":
      case "textarea":
        if (input.value.trim().length < 1) {
          isValid = false;
        }
        break;
      case "select":
        if (!input.value || input.value === "" || input.value === "0") {
          isValid = false;
        }
        break;
    }
  } else if (input.hasAttribute("required")) {
    isValid = false;
  }

  if (isValid) {
    removeErrorMessage(input);
  } else {
    showErrorMessage(input);
  }

  return isValid;
}

function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

function isValidPhone(phone) {
  const phoneRegex = /^[0-9\-]*$/;
  if (!phoneRegex.test(phone)) {
    return false;
  }
  const digitCount = phone.replace(/\-/g, "").length;
  return digitCount >= 8;
}

function showErrorMessage(input) {
  if (!input.classList.contains("error-input")) {
    input.classList.add("error-input");
  }
}

function removeErrorMessage(input) {
  input.classList.remove("error-input");
}

function validateFormOnSubmit(submitButton) {
  const form = submitButton.closest("form");
  if (!form) return true;

  const requiredInputs = form.querySelectorAll(`
    input[required][type="text"],
    input[required][type="email"], 
    input[required][type="password"],
    input[required][type="tel"],
    textarea[required],
    select[required]
  `);

  let isValid = true;

  requiredInputs.forEach((input) => {
    const type =
      input.tagName.toLowerCase() === "textarea"
        ? "textarea"
        : input.tagName.toLowerCase() === "select"
        ? "select"
        : input.type;
    const inputIsValid = validateInputByType(input, type);

    if (!inputIsValid) {
      isValid = false;
    }
  });

  return isValid;
}

function initFormValidation() {
  let isValidating = false;

  document.addEventListener(
    "invalid",
    function (e) {
      const input = e.target;
      if (!input || input.classList?.contains("no-js-validation")) return;
      e.preventDefault();
      input.classList?.add("error-input");
      notifyValidation(input);
    },
    true
  );

  document.addEventListener(
    "input",
    debounce(function (e) {
      const input = e.target;
      const supportedTypes = ["text", "email", "password", "tel"];
      const isTextarea = input.tagName.toLowerCase() === "textarea";
      const isSelect = input.tagName.toLowerCase() === "select";

      if (
        input.hasAttribute("required") &&
        (supportedTypes.includes(input.type) || isTextarea || isSelect) &&
        !input.classList.contains("no-js-validation")
      ) {
        isValidating = true;
        const type = isTextarea ? "textarea" : isSelect ? "select" : input.type;
        validateInputByType(input, type);
        isValidating = false;
      }
    }, 1500),
    true
  );

  document.addEventListener(
    "focus",
    function (e) {
      const input = e.target;
      const supportedTypes = ["text", "email", "password", "tel"];
      const isTextarea = input.tagName.toLowerCase() === "textarea";
      const isSelect = input.tagName.toLowerCase() === "select";
      if (
        input.hasAttribute("required") &&
        (supportedTypes.includes(input.type) || isTextarea || isSelect) &&
        input.classList.contains("error-input") &&
        !input.classList.contains("no-js-validation")
      ) {
        const type = isTextarea ? "textarea" : isSelect ? "select" : input.type;
        let shouldRemoveError = false;

        if (input.value.trim()) {
          switch (type) {
            case "email":
              shouldRemoveError = isValidEmail(input.value);
              break;
            case "tel":
              shouldRemoveError = isValidPhone(input.value);
              break;
            case "text":
            case "textarea":
              shouldRemoveError = input.value.trim().length >= 1;
              break;
            case "select":
              shouldRemoveError =
                input.value && input.value !== "" && input.value !== "0";
              break;
          }
        }

        if (shouldRemoveError) {
          removeErrorMessage(input);
        }
      }
    },
    true
  );

  document.addEventListener(
    "blur",
    function (e) {
      const input = e.target;
      const supportedTypes = ["text", "email", "password", "tel"];
      const isTextarea = input.tagName.toLowerCase() === "textarea";
      const isSelect = input.tagName.toLowerCase() === "select";

      if (
        input.hasAttribute("required") &&
        (supportedTypes.includes(input.type) || isTextarea || isSelect) &&
        !input.classList.contains("no-js-validation")
      ) {
        const type = isTextarea ? "textarea" : isSelect ? "select" : input.type;
        validateInputByType(input, type);
      }
    },
    true
  );

  document.addEventListener(
    "click",
    function (e) {
      const button = e.target;

      if (
        button.type === "submit" ||
        button.getAttribute("type") === "submit" ||
        (button.tagName.toLowerCase() === "button" &&
          button.closest("form") &&
          !button.type) ||
        (button.tagName.toLowerCase() === "input" && button.type === "submit")
      ) {
        isValidating = true;
        const isFormValid = validateFormOnSubmit(button);
        isValidating = false;

        if (!isFormValid) {
          e.preventDefault();
          e.stopPropagation();

          const form = button.closest("form");
          if (form) {
            const firstErrorInput = form.querySelector(".error-input");
            if (firstErrorInput) {
              firstErrorInput.focus({ preventScroll: false });
              firstErrorInput.classList.add("error-input");
              notifyValidation(firstErrorInput);
            }
          }
          return false;
        }
      }
    },
    true
  );
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initFormValidation);
} else {
  initFormValidation();
}
